import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  HTML_LANG,
  LOCALES,
  type Locale,
  type Localized,
  type LocalizedList,
} from "./types";

const STORAGE_KEY = "locale";

function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

export const LANG_PARAM = "lang";

/** `?lang=pt` — an explicit link, so it outranks anything remembered. */
function localeFromUrl(): Locale | null {
  if (typeof window === "undefined") return null;
  try {
    const value = new URL(window.location.href).searchParams.get(LANG_PARAM);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * URL wins, then the stored choice, then the browser's languages.
 *
 * The URL comes first so shared and crawled links land on the language they
 * advertise — this is what the hreflang alternates in index.html point at, and
 * a stored preference overriding them would show Google the wrong content.
 *
 * Storage throws outright in some contexts (private mode, blocked site data),
 * so every access is guarded.
 */
export function detectLocale(): Locale {
  const fromUrl = localeFromUrl();
  if (fromUrl) return fromUrl;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* fall through to browser preference */
  }

  if (typeof navigator === "undefined") return "en";

  // navigator.languages is ordered by preference; language is the single best.
  // Matching on the prefix covers pt, pt-BR and pt-PT alike.
  const preferred = navigator.languages?.length
    ? navigator.languages
    : [navigator.language].filter(Boolean);

  return preferred.some((tag) => tag?.toLowerCase().startsWith("pt")) ? "pt" : "en";
}

type LocaleContextValue = {
  locale: Locale;
  setLocale: (next: Locale) => void;
  toggleLocale: () => void;
  /** Pick the active string out of a Localized value. */
  t: (value: Localized) => string;
  tList: (value: LocalizedList) => readonly string[];
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* preference just won't persist */
    }
  }, []);

  const toggleLocale = useCallback(() => {
    setLocaleState((current) => {
      const next = LOCALES[(LOCALES.indexOf(current) + 1) % LOCALES.length];
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* preference just won't persist */
      }
      return next;
    });
  }, []);

  // Assistive tech picks pronunciation from this, so it has to track the
  // content rather than staying at the document's authored language.
  useEffect(() => {
    document.documentElement.lang = HTML_LANG[locale];
  }, [locale]);

  // Keep the address bar honest so a copied link reopens in the same language,
  // and so it matches the hreflang alternates. replaceState rather than
  // pushState: switching language is not a navigation, and stacking history
  // entries would make Back feel broken.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const current = url.searchParams.get(LANG_PARAM);

      // English is the default and x-default target, so it stays bare.
      if (locale === "en") {
        if (current === null) return;
        url.searchParams.delete(LANG_PARAM);
      } else {
        if (current === locale) return;
        url.searchParams.set(LANG_PARAM, locale);
      }

      window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    } catch {
      /* history unavailable — the toggle still works, the URL just won't track */
    }
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      toggleLocale,
      t: (v: Localized) => v[locale],
      tList: (v: LocalizedList) => v[locale],
    }),
    [locale, setLocale, toggleLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used inside <LocaleProvider>");
  return ctx;
}
